import { jsPDF } from 'jspdf'
import { isEmpty, isUndefined } from 'lodash'
import { computed, nextTick, ref, toValue, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import CustomDashboardApiHandler from '@/api/custom-dashboard'
import { handleError } from '@/utils/error-handling'
import {
  addExtraSpaceForFirefox,
  addVisibilityForLogo,
  alignOkrIcons,
  fixIndicatorPanel,
  ignoreElementsForExport,
  replaceAvatars,
  replaceSvgIcons,
  replaceTaskIcons,
  useHtml2canvasWrapper
} from '@/utils/html2canvas-helpers'
import { memoizeGetCssVariableValue } from '@/utils/memoizations'
import { NOTIFICATION_TYPES, showNotify } from '@/utils/notify'
import { ascSortValue, descSortValue } from '@/utils/sort-options'
import { TABLE_COLUMNS_KEYS } from '@/utils/table-columns'

export const CUSTOM_DASHBOARD_ITEM_TYPES = {
  DEFAULT: 'default',
  HOMEPAGE: 'homepage'
}

export const HORIZONTAL = 'HORIZONTAL'
export const VERTICAL = 'VERTICAL'
export const LAYOUT_VIEW_TYPES = {
  [HORIZONTAL]: 1,
  [VERTICAL]: 2
}

export const LAYOUT_NAMES_BY_TYPE_ID = Object.fromEntries(
  Object.entries(LAYOUT_VIEW_TYPES).map(item => item.reverse())
)

export const MAX_OBJECTIVES = 50
export const MIN_OBJECTIVES = 3

export const getAddObjectiveButtonDisabledState = ({ alreadyAddedObjectivesCount = 0 } = {}) => {
  const isLimitExceeded = alreadyAddedObjectivesCount >= MAX_OBJECTIVES
  const { t } = useI18n()

  return {
    disabled: isLimitExceeded,
    tooltipContent: isLimitExceeded
      ? t('limitations.entity.limit_exceeded', {
          limit: MAX_OBJECTIVES,
          entity: t('objectives.okr', MAX_OBJECTIVES)
        })
      : null
  }
}

export const USER_SHARING_ROLE = {
  OWNER: 1,
  VIEWER: 2,
  EDITOR: 3
}

const { FAVORITES, NAME, OWNER, VISIBILITY, GROUPS, CHECKIN } = TABLE_COLUMNS_KEYS

export const CUSTOM_DASHBOARD_SORT_TYPES = {
  FAVORITE_ASC: {
    id: 1,
    sortBy: ascSortValue,
    sortField: FAVORITES
  },
  FAVORITE_DESC: {
    id: 2,
    sortBy: descSortValue,
    sortField: FAVORITES
  },
  NAME_ACS: {
    id: 3,
    sortBy: ascSortValue,
    sortField: NAME
  },
  NAME_DESC: {
    id: 4,
    sortBy: descSortValue,
    sortField: NAME
  },
  OWNER_NAME_ASC: {
    id: 5,
    sortBy: ascSortValue,
    sortField: OWNER
  },
  OWNER_NAME_DESC: {
    id: 6,
    sortBy: descSortValue,
    sortField: OWNER
  },
  VISIBILITY_ASC: {
    id: 7,
    sortBy: ascSortValue,
    sortField: VISIBILITY
  },
  VISIBILITY_DESC: {
    id: 8,
    sortBy: descSortValue,
    sortField: VISIBILITY
  }
}

export const MODULE_TYPE_IDS = {
  OKR: 1,
  KPI: 2,
  TEXT: 3,
  CHECK_IN: 4
}

export const CUSTOM_DASHBOARD_MODULE_TYPES_FOR_TRACKING = {
  [MODULE_TYPE_IDS.OKR]: 'objective',
  [MODULE_TYPE_IDS.KPI]: 'progress',
  [MODULE_TYPE_IDS.TEXT]: 'text',
  [MODULE_TYPE_IDS.CHECK_IN]: 'check-in'
}

export const CUSTOM_DASHBOARD_QUERY_KEYS = {
  OWNER_IDS: 'ownerIds',
  SORT_BY: 'sortBy',
  SORT_FIELD: 'sortField'
}

export const getLayoutTypeId = [
  {
    // label: i18n.global.t('dashboard.horizontal'),
    label: '',
    value: LAYOUT_VIEW_TYPES[HORIZONTAL],
    icon: 'horizontal-view',
    tooltip: 'dashboard.horizontal'
  },
  {
    // label: i18n.global.t('dashboard.vertical'),
    label: '',
    value: LAYOUT_VIEW_TYPES[VERTICAL],
    icon: 'vertical-view',
    tooltip: 'dashboard.vertical'
  }
]

export const CUSTOM_DASHBOARD_NAME_MAX_LENGTH = 150

export const useBulkAddToCustomDashboard = () => {
  const dashboardsForAddElements = ref([])
  const elementIdsToAdd = ref([])
  const isGetDashboardsForAddLoading = ref(false)
  const isShowBulkAddToDashboardModal = ref(false)
  const { t } = useI18n()

  const proceedAddToDashboard = async (elementIds = []) => {
    elementIdsToAdd.value = [...elementIds]
    const api = new CustomDashboardApiHandler()
    isGetDashboardsForAddLoading.value = true
    try {
      dashboardsForAddElements.value = await api.getDashboardsForAddElements({
        elementIds
      })
      await nextTick()

      if (isEmpty(dashboardsForAddElements.value)) {
        showNotify({
          type: NOTIFICATION_TYPES.WARNING,
          expanded: true,
          content: t('dashboard.no_dashboards_warning')
        })
      } else {
        isShowBulkAddToDashboardModal.value = true
      }
    } catch (error) {
      handleError({ error })
    } finally {
      isGetDashboardsForAddLoading.value = false
    }
  }

  const closeAddToCustomDashboardModal = () => {
    isShowBulkAddToDashboardModal.value = false
    dashboardsForAddElements.value = []
    elementIdsToAdd.value = []
  }

  return {
    proceedAddToDashboard,
    dashboardsForAddElements,
    isGetDashboardsForAddLoading,
    isShowBulkAddToDashboardModal,
    closeAddToCustomDashboardModal,
    elementIdsToAdd
  }
}

export const PROGRESS_MODULE_VIEW_GRANULATION = {
  WEEKLY: {
    value: 1,
    label: 'units.week',
    oneDayWidth: 26,
    minPeriodDurationToShowContent: 3 // 3 and more days
  },
  MONTHLY: {
    value: 2,
    label: 'units.month',
    oneDayWidth: 6,
    minPeriodDurationToShowContent: 13 // 13 and more days
  },
  QUARTERLY: {
    value: 3,
    label: 'units.quarter',
    oneDayWidth: 2,
    minPeriodDurationToShowContent: 39 // 39 and more days
  }
}

export const getProgressModuleGranulationSettingsById = ({
  granulationId = PROGRESS_MODULE_VIEW_GRANULATION.WEEKLY.value
} = {}) => {
  return (
    Object.values(PROGRESS_MODULE_VIEW_GRANULATION).find(
      settings => settings.value === granulationId
    ) || PROGRESS_MODULE_VIEW_GRANULATION.WEEKLY
  )
}

export const PROGRESS_MODULE_COLUMN_IDS = {
  [NAME]: 1,
  [OWNER]: 2,
  [GROUPS]: 3,
  [CHECKIN]: 4
}

export const ALWAYS_ENABLED_PROGRESS_MODULE_COLUMNS = [PROGRESS_MODULE_COLUMN_IDS[NAME]]

export const CONFIGURABLE_PROGRESS_MODULE_COLUMNS = [
  PROGRESS_MODULE_COLUMN_IDS[OWNER],
  PROGRESS_MODULE_COLUMN_IDS[GROUPS],
  PROGRESS_MODULE_COLUMN_IDS[CHECKIN]
]

export const DEFAULT_ENABLED_PROGRESS_MODULE_COLUMNS = [...CONFIGURABLE_PROGRESS_MODULE_COLUMNS]

export const CUSTOM_DASHBOARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT = 4

export const CUSTOM_DASHBOARD_PROGRESS_VIEW_COLUMN_COUNT_OPTIONS = [4, 5, 6, 7, 8]

export const CUSTOM_DASHBOARD_MODULE_ATTRIBUTES = {
  LAYOUT_TYPE_ID: {
    attributeKey: 'layoutTypeId',
    attributeDefaultValueKey: 'defaultLayoutTypeId',
    typeId: 1,
    defaultValue: LAYOUT_VIEW_TYPES[HORIZONTAL]
  },

  GRANULATION_ID: {
    attributeKey: 'granulationId',
    attributeDefaultValueKey: 'defaultGranulationId',
    typeId: 2,
    defaultValue: PROGRESS_MODULE_VIEW_GRANULATION.MONTHLY.value
  },

  ACTIVE_COLUMNS: {
    attributeKey: 'enabledColumns',
    typeId: 3,
    defaultValue: [...DEFAULT_ENABLED_PROGRESS_MODULE_COLUMNS]
  },

  COLUMN_COUNT: {
    attributeKey: 'columnCount',
    typeId: 4,
    defaultValue: CUSTOM_DASHBOARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT
  }
}

export const getAttributeByTypeId = (
  typeId = CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID.typeId
) => {
  return Object.values(CUSTOM_DASHBOARD_MODULE_ATTRIBUTES).find(
    attribute => attribute.typeId === typeId
  )
}

const normalizeDashboardAttributes = ({ attributes = [], moduleParams = {} } = {}) => {
  return attributes.reduce((acc, attr) => {
    const attributeParams = getAttributeByTypeId(attr.typeId)

    if (attributeParams) {
      const { attributeKey, defaultValue } = attributeParams

      let resolvedValue = attr.value ?? defaultValue

      const additionalAttributes = {}

      if (attributeParams.typeId === CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID.typeId) {
        additionalAttributes[
          CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID.attributeDefaultValueKey
        ] = resolvedValue

        const granulationIdFromModuleParams = moduleParams[attributeKey]

        if (granulationIdFromModuleParams) {
          // overwrite saved value by requested value
          // to avoid changing granulationId in the module on reload dashboard with params
          resolvedValue = granulationIdFromModuleParams
        }
      }

      if (attributeParams.typeId === CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID.typeId) {
        additionalAttributes[
          CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID.attributeDefaultValueKey
        ] = resolvedValue
      }

      return {
        ...acc,
        [attributeKey]: resolvedValue,
        ...additionalAttributes
      }
    }

    return acc
  }, {})
}

export const normalizeDashboardModule = ({ module = {}, moduleParams = [] } = {}) => {
  const currentModuleParams = moduleParams.find(param => param.moduleId === module.id) || {}

  return {
    ...module,
    ...normalizeDashboardAttributes({
      attributes: module.attributes,
      moduleParams: currentModuleParams
    }),
    elements: module.elements.map(item => {
      const { element, orderValue, ...rest } = item

      return {
        ...element,
        ...rest,
        orderValueInModule: orderValue
      }
    })
  }
}

export const normalizeDashboardItem = ({ dashboardItem = {}, moduleParams = [] } = {}) => {
  return {
    ...dashboardItem,
    modules: dashboardItem.modules.map(module => normalizeDashboardModule({ module, moduleParams }))
  }
}

/**
 * Check if current user is owner of dashboard
 * @param {import('vue').MaybeRefOrGetter} item
 * @returns {boolean}
 */
export const isDashboardOwner = item => {
  return toValue(item)?.currentUserRoleId === USER_SHARING_ROLE.OWNER
}

/**
 * Check if current user has editor role in dashboard
 * @param {import('vue').MaybeRefOrGetter} item
 * @returns {boolean}
 */
export const isDashboardEditor = item => {
  return toValue(item)?.currentUserRoleId === USER_SHARING_ROLE.EDITOR
}

export const getDashboardState = () => {
  const store = useStore()

  const dashboardItem = computed(() => store.state.dashboard.dashboardItem)
  const getOwner = computed(() => {
    const { ownerAvatarUrl, ownerId, ownerName } = dashboardItem.value

    return {
      ownerAvatarUrl,
      ownerId,
      ownerName
    }
  })

  const currentUser = computed(() => store.state.system.userData || {})
  const isOwner = computed(() => isDashboardOwner(dashboardItem))
  const isPluginAdmin = computed(() => store.state.pluginOptions.isPluginAdmin)
  const isEditor = computed(() => isDashboardEditor(dashboardItem))

  return {
    currentUser,
    isOwner,
    isEditor,
    isPluginAdmin,
    getOwner,
    dashboardItem
  }
}

export const useCustomDashboardModuleHelper = () => {
  const initModuleState = ({ moduleId = null, state = {} } = {}) => {
    if (moduleId) {
      customDashboardModulesState.value[moduleId] = {
        ...state
      }
    }
  }

  const toggleModuleLoading = (moduleId, value) => {
    if (moduleId) {
      customDashboardModulesState.value[moduleId].isLoading = isUndefined(value)
        ? !customDashboardModulesState.value[moduleId].isLoading
        : value
    }
  }

  const setModuleGranulationId = (
    moduleId,
    granulationId = CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.GRANULATION_ID.defaultValue
  ) => {
    if (moduleId && customDashboardModulesState.value[moduleId]) {
      customDashboardModulesState.value[moduleId].currentGranulationId = granulationId
    }
  }

  const setModuleLayoutTypeId = (
    moduleId,
    layoutTypeId = CUSTOM_DASHBOARD_MODULE_ATTRIBUTES.LAYOUT_TYPE_ID.defaultValue
  ) => {
    if (moduleId && customDashboardModulesState.value[moduleId]) {
      customDashboardModulesState.value[moduleId].currentLayoutTypeId = layoutTypeId
    }
  }

  const setColumnCount = (
    moduleId,
    columnCount = CUSTOM_DASHBOARD_PROGRESS_VIEW_DEFAULT_COLUMN_COUNT
  ) => {
    if (moduleId && customDashboardModulesState.value[moduleId]) {
      customDashboardModulesState.value[moduleId].columnCount = columnCount
    }
  }

  const destroyModuleState = moduleId => {
    if (moduleId) {
      delete customDashboardModulesState.value[moduleId]
    }
  }

  const customDashboardModulesState = ref({
    initModuleState,
    toggleModuleLoading,
    setModuleGranulationId,
    setModuleLayoutTypeId,
    setColumnCount,
    destroyModuleState
  })

  const modulesReferenceKey = 'modules-reference'
  const modulesReference = useTemplateRef(modulesReferenceKey)
  const store = useStore()

  const { html2canvasWrapper } = useHtml2canvasWrapper()

  const exportModuleToPdf = async ({ moduleId = 'unknown', moduleName = '' } = {}) => {
    const moduleSelector = `#${getCustomDashboardModuleSelectorId(moduleId)}`

    const targetModule = modulesReference.value.querySelector(moduleSelector)

    if (targetModule) {
      customDashboardModulesState.value[moduleId].isExportProceed = true
      await nextTick()
      const { offsetWidth: width, offsetHeight: height } = targetModule

      const customWidth = Math.min(width, 1200)

      await store.dispatch('system/toggleFullAppLoader', {
        flag: true,
        alpha: 1,
        backgroundColor: memoizeGetCssVariableValue('--white-color', '#ffffff'),
        circleColor: memoizeGetCssVariableValue('--dark-2', '#42526E')
      })
      await nextTick()

      // setTimeout is for avoid start of export before the loader is shown and for showing chart
      setTimeout(async () => {
        const canvas = await html2canvasWrapper(targetModule, {
          onclone: clonedNode => {
            const clonedModule = clonedNode.querySelector(moduleSelector)
            clonedModule.style.maxWidth = customWidth + 'px'
            addVisibilityForLogo(clonedModule)
            // const backgroundWrapper = clonedNode.querySelector('.cdi-PageWrapper')
            // backgroundWrapper.style.background = 'none'
            const hiddenNames = clonedNode.querySelectorAll('.ocn-Name-hidden')
            hiddenNames.forEach(item => {
              item.style['background-color'] = 'transparent'
              item.style['background-image'] = 'none'
            })

            clonedModule.querySelector('.cdm-Wrapper').style.border = 'none'

            addExtraSpaceForFirefox(clonedNode)
            alignOkrIcons(clonedNode)
            replaceAvatars(clonedNode)
            replaceTaskIcons(clonedNode)
            replaceSvgIcons(clonedNode)
            fixIndicatorPanel(clonedNode)
          },
          scale: 1.75,
          useCORS: true, // necessary to display base64 images
          imageTimeout: 0,
          height,
          width: customWidth,
          ignoreElements: ignoreElementsForExport()
        })
        const dataUrl = canvas.toDataURL('image/png')

        const pdfFile = new jsPDF({
          orientation: customWidth > height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [customWidth, targetModule.offsetHeight],
          compress: true
        })
        pdfFile.addImage(dataUrl, 'PNG', 0, 0, customWidth, targetModule.offsetHeight)

        const format = moduleName.includes('.') ? '.pdf' : ''

        pdfFile.save(`custom_dashboard_${moduleName}${format}`)
        customDashboardModulesState.value[moduleId].isExportProceed = false
        await store.dispatch('system/toggleFullAppLoader')
      })
    }
  }

  return {
    customDashboardModulesState,
    modulesReferenceKey,
    modulesReference,
    exportModuleToPdf
  }
}

export const getCustomDashboardModuleSelectorId = itemId => `custom-dashboard-module-${itemId}`

export const MODULE_LIMIT = 10

export const PROGRESS_MODULE_CELL_PLACEMENTS = {
  CUSTOM_DASHBOARD: 'CUSTOM_DASHBOARD',
  OKR_ELEMENT_FORM: 'OKR_ELEMENT_FORM',
  PROGRESS_VIEW: 'PROGRESS_VIEW'
}
